import { useEffect, useRef, useState } from "react";
import { NavLink, Link, useLocation } from "react-router-dom";
import { Menu, X, ArrowUpRight, ChevronDown } from "lucide-react";
import { SITE } from "@/constants/testIds";
import { MARKETS_NAV } from "@/data/locationContent";

const links = [
    { to: "/", label: "Index", testId: SITE.navHome, end: true },
    { to: "/who-we-are", label: "Who we are", testId: SITE.navWho },
    { to: "/what-we-do", label: "What we do", testId: SITE.navWhat },
    { to: "/snapshot", label: "Snapshot", testId: SITE.navSnapshot },
    { to: "/blog", label: "Blog", testId: SITE.navBlog },
    { to: "/contact", label: "Contact", testId: SITE.navContact },
];

export const Header = () => {
    const [scrolled, setScrolled] = useState(false);
    const [open, setOpen] = useState(false);
    const [marketsOpen, setMarketsOpen] = useState(false);
    const marketsRef = useRef(null);
    const location = useLocation();

    useEffect(() => {
        const onScroll = () => setScrolled(window.scrollY > 24);
        onScroll();
        window.addEventListener("scroll", onScroll);
        return () => window.removeEventListener("scroll", onScroll);
    }, []);

    useEffect(() => {
        setOpen(false);
        setMarketsOpen(false);
    }, [location.pathname]);

    useEffect(() => {
        const onClick = (e) => {
            if (marketsOpen && marketsRef.current && !marketsRef.current.contains(e.target)) {
                setMarketsOpen(false);
            }
        };
        document.addEventListener("mousedown", onClick);
        return () => document.removeEventListener("mousedown", onClick);
    }, [marketsOpen]);

    const isMarketsActive = location.pathname.startsWith("/forex-marketing-agency") || location.pathname.startsWith("/agence-marketing-forex");

    return (
        <header
            data-testid={SITE.nav}
            className={`fixed top-0 inset-x-0 z-50 transition-all duration-500 ${
                scrolled
                    ? "bg-brand-bg/80 backdrop-blur-xl border-b border-brand-primary/10"
                    : "bg-transparent border-b border-transparent"
            }`}
        >
            <div className="container-x flex items-center h-20">
                <div className="flex items-center gap-6 lg:gap-8 xl:gap-12 min-w-0 mr-auto">
                    <Link to="/" data-testid={SITE.navLogo} className="flex items-center group shrink-0" aria-label="Adx Fusion — home">
                        <img
                            src="/logo.png"
                            alt="Adx Fusion"
                            width="40"
                            height="40"
                            className="h-10 w-auto"
                            loading="eager"
                            decoding="async"
                        />
                    </Link>

                    <nav className="hidden lg:flex items-center gap-4 xl:gap-7">
                    {links.slice(0, 5).map((l) => (
                        <NavLink
                            key={l.to}
                            to={l.to}
                            end={l.end}
                            data-testid={l.testId}
                            className={({ isActive }) =>
                                `text-xs xl:text-sm uppercase tracking-[0.16em] xl:tracking-[0.18em] whitespace-nowrap transition-colors duration-300 ${
                                    isActive ? "text-brand-mist" : "text-brand-primary/70 hover:text-brand-mist"
                                }`
                            }
                        >
                            {l.label}
                        </NavLink>
                    ))}

                    {/* Markets dropdown */}
                    <div className="relative" ref={marketsRef}>
                        <button
                            data-testid="nav-link-markets"
                            onClick={() => setMarketsOpen((v) => !v)}
                            className={`inline-flex items-center gap-1.5 text-xs xl:text-sm uppercase tracking-[0.16em] xl:tracking-[0.18em] whitespace-nowrap transition-colors duration-300 ${
                                isMarketsActive || marketsOpen ? "text-brand-mist" : "text-brand-primary/70 hover:text-brand-mist"
                            }`}
                            aria-expanded={marketsOpen}
                            aria-haspopup="true"
                        >
                            Markets
                            <ChevronDown className={`w-3.5 h-3.5 transition-transform ${marketsOpen ? "rotate-180" : ""}`} />
                        </button>
                        {marketsOpen ? (
                            <div className="absolute right-0 mt-4 w-[520px] bg-brand-bg/95 backdrop-blur-xl border border-brand-primary/15 shadow-2xl">
                                <div className="grid grid-cols-2 divide-x divide-brand-primary/10">
                                    {Object.entries(MARKETS_NAV).map(([key, group]) => (
                                        <div key={key} className="p-6">
                                            <p className="overline mb-4">{group.label}</p>
                                            <ul className="space-y-2">
                                                {group.items.map((it) => (
                                                    <li key={it.slug}>
                                                        <NavLink
                                                            to={`/${it.slug}`}
                                                            data-testid={`nav-market-${it.slug}`}
                                                            className={({ isActive }) =>
                                                                `block py-2 text-base text-brand-frost hover:text-brand-primary transition-colors ${
                                                                    isActive ? "text-brand-primary" : ""
                                                                }`
                                                            }
                                                        >
                                                            {it.label}
                                                        </NavLink>
                                                    </li>
                                                ))}
                                            </ul>
                                        </div>
                                    ))}
                                </div>
                            </div>
                        ) : null}
                    </div>

                    <NavLink
                        to="/contact"
                        end={links[5].end}
                        data-testid={SITE.navContact}
                        className={({ isActive }) =>
                            `text-xs xl:text-sm uppercase tracking-[0.16em] xl:tracking-[0.18em] whitespace-nowrap transition-colors duration-300 ${
                                isActive ? "text-brand-mist" : "text-brand-primary/70 hover:text-brand-mist"
                            }`
                        }
                    >
                        Contact
                    </NavLink>
                </nav>
                </div>

                <div className="hidden lg:flex items-center shrink-0">
                    <Link
                        to="/contact"
                        data-testid={SITE.navCta}
                        className="group inline-flex items-center gap-2 text-xs xl:text-sm uppercase tracking-[0.16em] xl:tracking-[0.18em] text-brand-mist border border-brand-primary/40 px-4 xl:px-5 py-3 hover:bg-brand-primary hover:text-brand-bg transition-colors duration-300 whitespace-nowrap"
                    >
                        Start a project
                        <ArrowUpRight className="w-4 h-4 transition-transform group-hover:translate-x-0.5 group-hover:-translate-y-0.5" />
                    </Link>
                </div>

                <div className="lg:hidden flex items-center gap-1 shrink-0 ml-auto">
                    <button
                        data-testid={SITE.mobileToggle}
                        onClick={() => setOpen((v) => !v)}
                        className="p-2 text-brand-mist"
                        aria-label="Toggle menu"
                    >
                        {open ? <X className="w-6 h-6" /> : <Menu className="w-6 h-6" />}
                    </button>
                </div>
            </div>

            {open && (
                <div className="lg:hidden bg-brand-bg/95 backdrop-blur-xl border-t border-brand-primary/10 max-h-[80vh] overflow-y-auto">
                    <nav className="container-x flex flex-col py-8 gap-5">
                        {links.map((l) => (
                            <NavLink
                                key={l.to}
                                to={l.to}
                                end={l.end}
                                data-testid={`${l.testId}-mobile`}
                                className={({ isActive }) =>
                                    `text-3xl font-display tracking-tight ${
                                        isActive ? "text-brand-mist" : "text-brand-primary/80"
                                    }`
                                }
                            >
                                {l.label}
                            </NavLink>
                        ))}

                        <div className="pt-4 border-t border-brand-primary/10">
                            <p className="overline mb-4">Markets</p>
                            <ul className="space-y-3">
                                {Object.entries(MARKETS_NAV).map(([key, group]) => (
                                    <li key={key}>
                                        <p className="text-xs uppercase tracking-[0.18em] text-brand-primary/60 mb-2">
                                            {group.label}
                                        </p>
                                        <ul className="space-y-1.5">
                                            {group.items.map((it) => (
                                                <li key={it.slug}>
                                                    <NavLink
                                                        to={`/${it.slug}`}
                                                        data-testid={`nav-market-${it.slug}-mobile`}
                                                        className="block text-lg text-brand-frost hover:text-brand-primary transition-colors"
                                                    >
                                                        {it.label}
                                                    </NavLink>
                                                </li>
                                            ))}
                                        </ul>
                                    </li>
                                ))}
                            </ul>
                        </div>

                        <Link to="/contact" className="btn-primary mt-4 w-fit" data-testid={`${SITE.navCta}-mobile`}>
                            Start a project <ArrowUpRight className="w-4 h-4" />
                        </Link>
                    </nav>
                </div>
            )}
        </header>
    );
};
